import { Component, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, interval, Subject, switchMap } from 'rxjs';
import { downloadBlob } from 'src-ironside/helpers/file';
import { ValidateFileLogistic } from 'src-ironside/repositories/import-logistic';
import { DynamicProductParam, Product } from 'src-ironside/repositories/product.repository';
import { Supplier, SupplierRepository } from 'src-ironside/repositories/supplier.repository';
import { UserService } from 'src/app/admin/core/services/user.service';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';
import { ParametreNomtableService } from 'src/app/shared/services/parametre-nomtable.service';
import { environment } from 'src/environments/environment';
import {
    DataValidate,
    FrameType,
    ImportResult,
    ProductByLogisticVariable,
    WorkflowLogisticVariablePayload,
    WorkflowStatus,
} from '../models/export-logistic.interface';
import { MessageService } from 'primeng/api';
import { importSummaryArray } from '../constant/import-logistic.constant';
import { TaskService } from 'src/app/shared/services/task.service';

@Component({
    selector: 'app-content-logistic-import',
    templateUrl: './content-logistic-import.component.html',
    styleUrls: ['./content-logistic-import.component.scss'],
    providers: [SupplierRepository, MessageService],
})
export class ContentLogisticImportComponent implements OnInit {
    private productFilterSubject = new Subject<string>();
    private locale =
        environment.languagesExtranet.find((lang: any) => lang?.code === this.translate.currentLang)
            ?.locale || 'en_GB';

    suppliers: Supplier[] = [];
    keywordSearchSupplier: string = '';
    selectedSupplier: Supplier | null = null;
    filteredSupplier: Product[] = [];

    selectedProducts: Product[] = [];
    keywordSearchProduct: string = '';
    isExecuting: boolean = false;
    importSummaryArray = importSummaryArray;
    pollingSubscription: any
    selectedFileLogistic: {
        file: File | null;
        fileName: string;
    } = {
            file: null,
            fileName: '',
        };
    showSelect: boolean = false;

    productByLogisticVariable: ProductByLogisticVariable = {
        content: [],
        totalElements: 0,
        page: 1,
        pageSize: 100000,
        loading: false,
    };

    dataValidate: DataValidate = {
        loading: false,
        data: null,
    };
    taskName = "Importer Logistics Produits Ironside"
    statusList: WorkflowStatus[] = [];
    email: string = '';
    frameRepository: string = '';
    frameType: FrameType;
    private updateTaskSubject = new Subject<{ taskName: any; taskStatus: any }>();

    constructor(
        private supplierRepository: SupplierRepository,
        private dynamicDocumentService: DynamicDocumentService,
        private translate: TranslateService,
        private parametreNomtableService: ParametreNomtableService,
        private userService: UserService,
        private messageService: MessageService,
        private taskservice: TaskService,
    ) {
        this.productFilterSubject
            .pipe(debounceTime(500), distinctUntilChanged())
            .subscribe((value: string) => {
                this.fetchProductByLogisticVariable(value, true);
            });

        this.userService.getCurrentUser().then((user) => {
            this.email = user.email;
        });
    }



    ngOnInit(): void {
        this.fetchSuppliers();
        this.fetchProductByLogisticVariable();
        this.loadStatus();
        this.getFrameType();
    }

    filteredSuppliers(): any[] {
        const filter = this.keywordSearchSupplier?.toLowerCase() || '';
        return this.suppliers.filter((supplier) => supplier.title.toLowerCase().includes(filter));
    }

    isAllSelected(): boolean {
        const filtered = this.filteredSuppliers();
        return (
            filtered.length > 0 &&
            filtered.every((supplier) => this.selectedProducts.includes(supplier))
        );
    }

    isIndeterminate(): boolean {
        const filtered = this.productByLogisticVariable.content;
        const selectedCount = filtered.filter((supplier) =>
            this.selectedProducts.includes(supplier)
        ).length;
        return selectedCount > 0 && selectedCount < filtered.length;
    }

    toggleSelectAll(event: MatCheckboxChange): void {
        const filtered = this.productByLogisticVariable.content;

        if (event.checked) {
            const newSelections = filtered.filter((s) => !this.selectedProducts.includes(s));
            this.selectedProducts = [...this.selectedProducts, ...newSelections];
        } else {
            this.selectedProducts = this.selectedProducts.filter((s) => !filtered.includes(s));
        }
    }

    // Get tooltip text for selected products
    getSelectedSuppliersTooltip(): string {
        if (!this.selectedProducts || this.selectedProducts.length === 0) {
            return 'Aucun article sélectionné';
        }
        return this.selectedProducts.map((product) => `${product.referenceProduct}`).join(', ');
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            const fileName = file.name;
            const fileExtension = fileName.split('.').pop()?.toLowerCase();

            // Kiểm tra file extension
            const allowedExtensions = ['xlsx', 'xls', 'csv'];

            if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: `Le fichier sélectionné n’est pas valide. Veuillez choisir un fichier au format .xls, .xlsx ou .csv.`,
                });

                // Reset input file
                input.value = '';
                this.selectedFileLogistic = {
                    file: null,
                    fileName: '',
                };
                return;
            }

            this.selectedFileLogistic = {
                file: file,
                fileName: fileName,
            };
        }
    }

    onProductFilterChange(value: string): void {
        this.productByLogisticVariable.page = 1;
        this.productFilterSubject.next(value);
    }

    onSupplierFilterChange(value: string): void {
        const keyword = value?.toLowerCase().trim();
        this.filteredSupplier = this.suppliers.filter((supplier) =>
            supplier?.title?.toLowerCase().includes(keyword)
        );
    }

    onClickSupplierFilter() { }

    // loadMoreSuppliers(): void {
    //     if (this.productByLogisticVariable.loading) return;
    //     this.productByLogisticVariable.page++;
    //     this.fetchProductByLogisticVariable();
    // }

    fetchSuppliers(): void {
        this.supplierRepository.find().subscribe({
            next: (res) => {
                this.suppliers = res.content;
                this.filteredSupplier = this.suppliers;
            },
            error: (error) => { },
        });
    }

    fetchProductByLogisticVariable(keyword?: string, isLoadMore?: boolean): void {

        this.productByLogisticVariable.loading = true;
        let filter: any[] = [];

        if (keyword) {
            filter = [
                {
                    fieldName: 'shortDescription.en_GB',
                    operator: 'OR',
                    conditions: [{ type: 'CONTAINS', value: keyword }],
                },
                {
                    fieldName: 'referenceProduct',
                    operator: 'OR',
                    conditions: [{ type: 'CONTAINS', value: keyword }],
                },
            ];
        }

        if (this.selectedSupplier) {
            filter.push({
                fieldName: 'linkToSupplier.documentId',
                operator: 'OR',
                conditions: [{ type: 'EQUALS', value: this.selectedSupplier.id }],
            });
        }

        const body = {
            filters: filter,
            fields: [],
            locales: [this.locale],
            sorts: {
                referenceProduct: 'DESC',
                publishedOrder: 'DESC',
            },
            page: this.productByLogisticVariable.page,
            pageSize: this.productByLogisticVariable?.pageSize,
        };
        this.dynamicDocumentService.getProductByLogisticVariable(body).subscribe({
            next: (res: any) => {
                this.productByLogisticVariable = {
                    ...this.productByLogisticVariable,
                    content: isLoadMore
                        ? res?.content
                        : [...this.productByLogisticVariable.content, ...res?.content],
                    totalElements: res.totalElements || 0,

                    loading: false,
                };
            },
            error: (error) => {
                this.productByLogisticVariable.loading = false;
            },
        });
    }

    loadStatus() {
        this.parametreNomtableService.getParametreByTable('STATUT_WORKFLOW').subscribe({
            next: (res) => {
                this.statusList = res;
            },
            error: (error) => {
                console.log(error);
            },
        });
    }

    exportTemplateLogistic(): void {
        if (this.selectedProducts.length === 0) {
            this.messageService.add({
                severity: 'warn',
                detail: 'Veuillez sélectionner au moins un article',
            });

            return;
        }
        const filters = {
            fieldName: 'id',

            conditions: this.selectedProducts.map((product) => ({
                type: 'EQUALS',
                value: product.id,
            })),
        };
        const body: DynamicProductParam = {
            filters: [filters],
            fields: [
                'id',
                'longDescription',
                'shortDescription',
                'brand',
                'packaging',
                'state',
                'referenceProduct',
                'countryOfManufacture',
                'typeOfProduct',
                'creationDate',
                'department',
                'lastModifiedUser',
                'statusProduct',
                'newStartDate',
                'newEndDate',
            ],
            locales: [this.locale],
            page: 1,
            pageSize: this.selectedProducts.length,
        };
        this.dynamicDocumentService.exportTemplateLogistic(body).subscribe({
            next: (res: Blob) => {
                downloadBlob(res, 'xlsx', 'template-logistic');
            },
            error: (error) => {
                console.log(error);
            },
        });
    }

    uploadFileLogistic(): void {

        if (this.selectedFileLogistic.file === null || !this.selectedSupplier?.title) {
            this.messageService.add({
                severity: 'warn',
                detail: 'Veuillez sélectionner un fournisseur et un fichier',
            });

            return;
        }

        this.dataValidate = {
            loading: true,
            data: null,
        };
        this.dynamicDocumentService
            .uploadFileLogistic(this.selectedFileLogistic.file!, this.selectedSupplier?.title || '')
            .pipe(
                switchMap((res: { message: string }) => {
                    this.frameRepository = res.message;
                    const body: ValidateFileLogistic = {
                        frameRepository: res.message,
                        frameType: this.frameType,
                        supplier: {
                            documentName: 'dynamic_legal_entity',
                            documentId: this.selectedSupplier?.id || '',
                        },
                        trameName: this.selectedFileLogistic.file?.name || '',
                    };

                    return this.dynamicDocumentService.validateFileLogistic(body);
                })
            )
            .subscribe({
                next: (res: ImportResult) => {
                    this.dataValidate = {
                        loading: false,
                        data: {
                            ...res,
                            records: res.records,
                        },
                    };

                    this.importSummaryArray.forEach((item) => {
                        item.value = res[item.key as keyof ImportResult] as number;
                    });
                },
                error: (error) => {
                    this.dataValidate.loading = false;
                },

            });


    }

    stopPolling(): void {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
        }
    }
    startPolling(): void {
        this.pollingSubscription = interval(5000) // Poll every 5 seconds
            .pipe(
                switchMap(() => this.taskservice.getTaskStatus(this.taskName))
            )
            .subscribe((response: any) => {
                if (!response.executing) {
                    this.isExecuting = false;
                    this.stopPolling(); // Stop polling once the task is completed
                }
            });
    }

    downloadReport() {
        const content = this.formatFrameValidation(this.dataValidate.data as ImportResult);

        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `rapport-${this.selectedFileLogistic?.fileName.split('.')[0]}.txt`;

        link.click();

        URL.revokeObjectURL(link.href);
    }

    private formatFrameValidation(frame: ImportResult): string {
        let formatted = `Fournisseur : ${this.selectedSupplier?.title || 'Fournisseur inconnu'}\n`;
        formatted += `Trame logistique\n`;

        formatted += `Date : ${new Date().toISOString()}\n\n`;

        formatted += `Nombre de lignes traitées : ${frame.nbLignesTreated}\n`;
        formatted += `Nombre de lignes ignorées : ${frame.nbLignesIgnore}\n`;
        formatted += `Nombre de lignes valides : ${frame.nbLignesOk}\n`;
        formatted += `Nombre de lignes en alertes : ${frame.nbLignesWarn}\n`;
        formatted += `Nombre de lignes en erreur : ${frame.nbLignesError}\n`;

        const groupedBySheet = new Map<string, any[]>();

        frame.records.forEach((record: any) => {
            if (record && record.row) {
                if (!groupedBySheet.has(record.sheetname)) {
                    groupedBySheet.set(record.sheetname, []);
                }
                groupedBySheet.get(record.sheetname)!.push(record);
            }
        });

        groupedBySheet.forEach((records, sheetname) => {
            formatted += `Feuille: ${sheetname}\n`;
            records.forEach((record) => {
                if (record.type === 'IGNORED') {
                    formatted += `${record.row} - IGNORE\n`;
                } else {
                    formatted += `${record.row} - ${record.type} [${record.sheetname}${record.column ? ', colonne ' + record.column : ''
                        }] ${record.message}\n`;
                }
            });
            formatted += `\n`; // Blank line between sheets
        });

        formatted +=
            frame.articlesWithoutLogisticsInDatabase ??
            [].map((article) => `L'article ${article} n'a pas de tarif.`).join('\n') + '\n\n';

        return formatted;
    }

    onWorkflowLogisticVariable() {
        this.isExecuting = true;
        if (this.dataValidate.data?.nbLignesError && this.dataValidate.data?.nbLignesError > 0) {
            this.messageService.add({
                severity: 'error',
                detail: "Veuillez corriger les erreurs avant d'importer la trame",
            });
            this.isExecuting = false;
            return;
        }
        const body: WorkflowLogisticVariablePayload = {
            supplier: {
                documentName: 'dynamic_legal_entity',
                documentId: this.selectedSupplier?.id || '',
            },
            /*  submissionDate: new Date().toISOString(), */
            frameType: {
                parameterId: this.frameType.id || '',
            },
            status: {
                parameterId: this.statusList.find((status) => status.valeur === 'PUBLIE')?.id || '',
            },
            userImported: this.email,
            publicationDate: Date.now(),
            frameRepository: this.frameRepository || '',
            frameName: this.selectedFileLogistic.fileName || '',
        };

        this.dynamicDocumentService.workflowLogisticVariable(body).subscribe({
            next: (res) => {
                this.messageService.add({
                    severity: 'success',
                    detail: 'La trame est en cours d’import…',
                });
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    detail: "Une erreur est survenue lors de l'importation de la trame",
                });
            },
        });
        this.startPolling();
    }

    getFrameType() {
        this.dynamicDocumentService.getFrameType().subscribe({
            next: (res) => {
                this.frameType = res.find(
                    (frameType) => frameType.valeur === 'logistics'
                ) as FrameType;
            },
            error: (error) => {
                console.log(error);
            },
        });
    }
}
